import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { inr, dateFmt } from "@/lib/format";
import { KpiCard, PageLoader, EmptyState, StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PiggyBank, Wallet, Send, Loader2, CheckCircle2 } from "lucide-react";

export default function Redeem() {
  const qc = useQueryClient();
  const { data: overview } = useQuery({ queryKey: ["author-overview", "all", "all", "all"], queryFn: async () => (await api.get("/author/overview")).data });
  const { data: reqs, isLoading } = useQuery({ queryKey: ["author-redemptions"], queryFn: async () => (await api.get("/author/redemptions")).data });

  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [method, setMethod] = useState("upi");
  const [upiId, setUpiId] = useState("");
  const [accName, setAccName] = useState("");
  const [accNo, setAccNo] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const available = overview?.available_profit ?? 0;

  const submit = useMutation({
    mutationFn: async () => (await api.post("/author/redemptions", {
      amount: parseFloat(amount), note, payout_method: method,
      upi_id: method === "upi" ? upiId : null,
      account_name: method === "bank" ? accName : null,
      account_number: method === "bank" ? accNo : null,
      ifsc: method === "bank" ? ifsc : null,
    })).data,
    onSuccess: () => {
      setConfirmOpen(true);
      setAmount(""); setNote(""); setUpiId(""); setAccName(""); setAccNo(""); setIfsc("");
      qc.invalidateQueries({ queryKey: ["author-redemptions"] });
      qc.invalidateQueries({ queryKey: ["author-overview"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const amt = parseFloat(amount);
  const amtValid = amt > 0 && amt <= available + 0.01;
  const payoutValid = method === "upi" ? upiId.trim() : (accName.trim() && accNo.trim() && ifsc.trim());
  const valid = amtValid && payoutValid;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <KpiCard testid="redeem-available" label="Redeemable Now" value={inr(available)} icon={PiggyBank} accent delay={0} />
        <KpiCard testid="redeem-redeemed" label="Total Redeemed" value={inr(overview?.redeemed ?? 0)} icon={Wallet} delay={60} />
        <KpiCard testid="redeem-pending" label="Pending" value={inr(overview?.pending_redemption ?? 0)} icon={Send} delay={120} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:p-8 lg:col-span-1 h-fit">
          <h3 className="font-head text-lg font-medium">Request a payout</h3>
          <p className="text-sm text-muted-foreground mt-1">Withdraw from your redeemable profit.</p>
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Amount (₹)</Label>
              <Input data-testid="redeem-amount" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Enter amount" />
              <p className="text-xs text-muted-foreground">Available: <span className="font-medium text-foreground">{inr(available)}</span></p>
            </div>
            <div className="space-y-2">
              <Label>Payout method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="payout-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="bank">Bank transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {method === "upi" ? (
              <div className="space-y-2">
                <Label>UPI ID</Label>
                <Input data-testid="payout-upi" value={upiId} onChange={(e) => setUpiId(e.target.value)} placeholder="name@bank" />
              </div>
            ) : (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Account holder name</Label>
                  <Input data-testid="payout-acc-name" value={accName} onChange={(e) => setAccName(e.target.value)} placeholder="As per bank" />
                </div>
                <div className="space-y-2">
                  <Label>Account number</Label>
                  <Input data-testid="payout-acc-no" value={accNo} onChange={(e) => setAccNo(e.target.value)} placeholder="Account number" />
                </div>
                <div className="space-y-2">
                  <Label>IFSC code</Label>
                  <Input data-testid="payout-ifsc" value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} placeholder="e.g. HDFC0001234" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Note (optional)</Label>
              <Textarea data-testid="redeem-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any additional details" className="min-h-[80px]" />
            </div>
            <Button data-testid="submit-redemption" onClick={() => submit.mutate()} disabled={!valid || submit.isPending} className="w-full gap-2">
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit request
            </Button>
            {amount && !amtValid && <p className="text-xs text-destructive">Enter a valid amount within your available balance.</p>}
          </div>
        </Card>

        <Card className="lg:col-span-2 overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h3 className="font-head font-medium">Request history</h3>
          </div>
          {isLoading ? (
            <PageLoader />
          ) : reqs.length === 0 ? (
            <EmptyState icon={Wallet} title="No requests yet" desc="Your redemption requests will appear here." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50 hover:bg-muted/50">
                  <TableHead>Date</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reqs.map((r) => (
                  <TableRow key={r.id} data-testid={`my-redemption-${r.id}`}>
                    <TableCell className="text-muted-foreground">{dateFmt(r.created_at)}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">{r.note || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.transaction_id || "—"}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{inr(r.amount)}</TableCell>
                    <TableCell><StatusBadge status={r.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md" data-testid="redemption-confirm-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-emerald-100 dark:bg-emerald-500/15 flex items-center justify-center shrink-0">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <DialogTitle className="font-head">Request sent</DialogTitle>
            </div>
            <DialogDescription className="pt-2 text-[15px] leading-relaxed">
              Your request has been sent. It will take 4–5 days to process after verification.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="redemption-confirm-ok" onClick={() => setConfirmOpen(false)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
